import { Link } from 'react-router-dom';
import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material';

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** What Wrangler made of the disqualifications, when the survey uses Wrangler. */
const WranglerOutcome = ({ wrangler, surveyId }) => {
  if (!wrangler || wrangler.status === 'not_used') return null;
  const setupLink = (
    <Button component={Link} to={`/survey/${surveyId}/wrangler?tab=setup`} color="inherit" size="small"
      sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
      Open Wrangler setup
    </Button>
  );
  const noSession = wrangler.without_session_id || [];
  if (wrangler.status === 'error') {
    return (
      <Alert severity="error" sx={{ mt: 1.5 }} action={setupLink}>
        <strong>Wrangler wasn&apos;t told about these disqualifications.</strong> Its counts still include them until a
        refresh: run &ldquo;Refresh from Alchemer&rdquo; on the Wrangler page&apos;s Setup tab.
      </Alert>
    );
  }
  return (
    <Alert severity={noSession.length ? 'warning' : 'success'} sx={{ mt: 1.5 }} action={noSession.length ? setupLink : null}>
      Wrangler removed {plural(wrangler.disqualified, 'complete')} from its counts.
      {wrangler.not_complete > 0 && <> {plural(wrangler.not_complete, 'response')} weren&apos;t complete in Wrangler, so its counts didn&apos;t include them.</>}
      {wrangler.unknown > 0 && <> {plural(wrangler.unknown, 'response')} {wrangler.unknown === 1 ? "isn't" : "aren't"} known to Wrangler (for example, from before it was set up).</>}
      {noSession.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {plural(noSession.length, 'response')} had no session ID in Alchemer, so Wrangler couldn&apos;t be told; a
          refresh from Alchemer will catch {noSession.length === 1 ? 'it' : 'them'}. Response IDs: {noSession.join(', ')}
        </Box>
      )}
    </Alert>
  );
};

/**
 * Progress while disqualifying, then the outcome in Alchemer and in Wrangler. Shared by the four
 * pages that disqualify (duplicates, gibberish, AI detection, Disqualify IDs).
 */
const DisqualifyStatus = ({ state, surveyId }) => {
  if (state.running) {
    return (
      <Box sx={{ mt: 2 }}>
        {state.total > 0 && state.progress < state.total ? (
          <>
            <Typography variant="body2" color="text.secondary">Disqualifying… {state.progress}/{state.total}</Typography>
            <LinearProgress variant="determinate" value={(state.progress / state.total) * 100} sx={{ mt: 0.5 }} />
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              {state.progress > 0 ? 'Refreshing quotas and telling Wrangler…' : 'Starting…'}
            </Typography>
            <LinearProgress sx={{ mt: 0.5 }} />
          </>
        )}
      </Box>
    );
  }
  if (state.error) return <Alert severity="error" sx={{ mt: 2 }}>{state.error}</Alert>;
  if (!state.result) return null;
  const done = state.result.disqualified_responses || [];
  const failed = state.result.failed_responses || [];
  return (
    <Box sx={{ mt: 2 }}>
      <Alert severity={failed.length ? 'warning' : 'success'}>
        Disqualified {plural(done.length, 'response')} in Alchemer.
        {failed.length > 0 && <> {plural(failed.length, 'response')} couldn&apos;t be disqualified: {failed.join(', ')}.</>}
      </Alert>
      <WranglerOutcome wrangler={state.result.wrangler} surveyId={surveyId} />
    </Box>
  );
};

export default DisqualifyStatus;
